/**
 * Tenant AI usage report for a period (TMMPDM-0013).
 * TMMPDM-0045 (tmm-api 0109): an At our price column beside Cost in both tables — our AI price for
 * the usage (`atOurPriceMicro`). Where a row doesn't carry it, a plain "—". TMMPDM-0049: one "?"
 * on the column header explains it, not a tab stop in every cell. TMMPDM-0048: a product-wide
 * feature row with `atOurPricePartial` shows "Partial" from that flag alone. Person rows are
 * unchanged.
 * TMMPDM-0088 (TMMAPI-0151/0152/0153): people rows show Wallet usage (the same field), the wallet
 * Balance in the wallet's own currency (never the row's), a Wallet disclosure with the seven figures
 * as sent, the "AI suspended by the platform" chip from `aiSuspendedAt` alone, and Add AI credit…
 * offered from `aiCreditAvailable` alone.
 * TMMPDM-0109 (TMMAPI-0181): My Financial Assistant and the Support assistant are labelled; a feature
 * this console doesn't know shows its raw name. Their on/off switches are tmm-api environment
 * settings the contract doesn't serve, so nothing here shows or toggles them.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { EmptyState, ErrorState, useNotify } from '@/components/page';
import { FilterTabs } from '@/components/ui/FilterTabs';
import { adminApi } from '@/api/tmm/admin';
import type {
  AiFeature,
  AiWalletPeriodView,
  FeatureUsageRow,
  PersonUsageRow,
  UsageReport,
} from '@/api/tmm/types';
import { Amount, HelpPopover, KpiCard } from '@/components/ui';
import { NOT_SENT, formatDate, formatInstant, formatMoney, formatNumber } from '@/format';
import { StatusChip } from '@/status';
import { AiCreditDialog } from './AiCreditDialog';
import { periodFor, type PeriodKey } from '../dashboard/period';

const AI_USAGE_VIEWS = ['features', 'people'] as const;
type AiUsageViewKey = (typeof AI_USAGE_VIEWS)[number];
/** `view`, not `tab`: this panel sits on a partner leaf that already owns `tab`. */
const AI_USAGE_VIEW: UrlSchema<{ view: AiUsageViewKey }> = {
  view: { default: 'features', parse: oneOf(AI_USAGE_VIEWS) },
};

interface AiUsagePanelProps {
  slug: string;
}

const PERIOD_KEYS: PeriodKey[] = ['THIS_MONTH', 'LAST_30_DAYS', 'LAST_MONTH'];

const FEATURE_FALLBACK: Record<AiFeature, string> = {
  IMPORT_MAPPING: 'Import mapping',
  CATEGORY_SUGGESTION: 'Category suggestion',
  ENVELOPE_ANALYSIS: 'Envelope analysis',
  MEETING_SUMMARY: 'Meeting summary',
  MATCH_SUGGESTION: 'Match suggestion',
  DOMAIN_SETUP: 'Domain setup',
  PAYEE_SUGGESTION: 'Payee suggestion',
  // TMMPDM-0110: shown by its raw name until now.
  PAYEE_PATTERN: 'Payee patterns',
  // TMMPDM-0109 (TMMAPI-0181): the two assistant features.
  FINANCIAL_ASSISTANT: 'My Financial Assistant',
  SUPPORT_ASSISTANT: 'Support assistant',
};

/**
 * TMMPDM-0088 (fb14a1a): VA_ANALYSIS was renamed ENVELOPE_ANALYSIS. Usage recorded before the rename
 * may still carry the old code, so it reads with the new label (Operator ruling 2026-10-07).
 */
function featureCode(feature: AiFeature): AiFeature {
  return String(feature) === 'VA_ANALYSIS' ? 'ENVELOPE_ANALYSIS' : feature;
}

/** TMMPDM-0109: the English label, or, for a feature this console doesn't know, its raw name. */
function featureFallback(feature: AiFeature): string {
  const code = featureCode(feature);
  return Object.hasOwn(FEATURE_FALLBACK, code) ? FEATURE_FALLBACK[code] : String(feature);
}

function localDayInstants(fromDate: string, toDate: string): { from: string; to: string } {
  const fromParts = fromDate.split('-').map(Number);
  const toParts = toDate.split('-').map(Number);
  const fy = fromParts[0] ?? 1970;
  const fm = fromParts[1] ?? 1;
  const fd = fromParts[2] ?? 1;
  const ty = toParts[0] ?? 1970;
  const tm = toParts[1] ?? 1;
  const td = toParts[2] ?? 1;
  return {
    from: new Date(fy, fm - 1, fd, 0, 0, 0, 0).toISOString(),
    to: new Date(ty, tm - 1, td, 23, 59, 59, 999).toISOString(),
  };
}

/** Our price in the row's currency; absent on the wire → a plain "—", not a tab stop. */
function AtOurPrice({ row, locale }: { row: FeatureUsageRow | PersonUsageRow; locale: string }) {
  return <Amount micro={row.atOurPriceMicro} currency={row.currency} locale={locale} />;
}

/**
 * Product-wide feature rows only. "Partial" is `atOurPricePartial` and nothing else. Any other
 * missing figure stays "—". Person rows keep {@link AtOurPrice}.
 */
function FeatureAtOurPrice({ row, locale }: { row: FeatureUsageRow; locale: string }) {
  const { t } = useCopy();
  if (row.atOurPricePartial === true) {
    return t('aiUsage.atOurPricePartial', 'Partial');
  }
  return <AtOurPrice row={row} locale={locale} />;
}

/** One "?" on the column header. The feature table's help also explains Partial. */
function AtOurPriceHeader({ featureTable = false }: { featureTable?: boolean }) {
  const { t } = useCopy();
  const label = t('aiUsage.col.atOurPrice', 'At our price');
  const short = featureTable
    ? t(
        'aiUsage.atOurPricePartialHelp',
        "— means no offer prices this usage at our price. Partial means some people's usage isn't priced at our price, so no total is shown.",
      )
    : t('aiUsage.atOurPriceAbsent', '— means no offer prices this usage at our price.');
  return (
    <TableCell align="right">
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.25,
        }}
      >
        {label}
        <HelpPopover label={label} short={short} />
      </Box>
    </TableCell>
  );
}

function personLabel(row: PersonUsageRow): string {
  return row.displayName ?? row.email ?? NOT_SENT;
}

/** A wallet figure in the wallet's own currency; "—" when the wallet carries no currency. */
function WalletAmount({
  minor,
  wallet,
  locale,
}: {
  minor: number;
  wallet: AiWalletPeriodView;
  locale: string;
}) {
  if (!wallet.currency) return NOT_SENT;
  return <Amount minor={minor} currency={wallet.currency} locale={locale} />;
}

/** The seven wallet figures, each as sent. Nothing is summed or derived. */
function WalletDetail({ wallet, locale }: { wallet: AiWalletPeriodView; locale: string }) {
  const { t } = useCopy();
  const rows: [string, string, number][] = [
    ['purchases', t('aiUsage.wallet.purchases', 'Purchases'), wallet.purchasesMinor],
    ['recharges', t('aiUsage.wallet.recharges', 'Recharges'), wallet.rechargesMinor],
    ['topUps', t('aiUsage.wallet.topUps', 'Top-ups'), wallet.topUpsMinor],
    ['monthly', t('aiUsage.wallet.monthlyCredits', 'Monthly credits'), wallet.monthlyCreditsMinor],
    ['admin', t('aiUsage.wallet.adminCredits', 'Admin credits'), wallet.adminCreditsMinor],
    ['usage', t('aiUsage.wallet.usage', 'Usage'), wallet.usageMinor],
    ['balance', t('aiUsage.wallet.balance', 'Balance'), wallet.balanceMinor],
  ];
  return (
    <Box
      component="dl"
      sx={{
        display: 'grid',
        gridTemplateColumns: 'max-content max-content',
        columnGap: 3,
        rowGap: 0.5,
        m: 0,
      }}
    >
      {rows.map(([key, label, minor]) => (
        <Fragment key={key}>
          <Typography component="dt" variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, textAlign: 'right' }}>
            <WalletAmount minor={minor} wallet={wallet} locale={locale} />
          </Typography>
        </Fragment>
      ))}
    </Box>
  );
}

/** "AI suspended by the platform" from `aiSuspendedAt` alone; the date is in its tooltip. */
function SuspendedChip({ at, locale }: { at: string; locale: string }) {
  const { t } = useCopy();
  return (
    <Tooltip
      describeChild
      title={t('aiUsage.suspendedSince', 'Since {{date}}', { date: formatInstant(at, locale) })}
    >
      <StatusChip
        tone="warning"
        tabIndex={0}
        label={t('aiUsage.suspended', 'AI suspended by the platform')}
      />
    </Tooltip>
  );
}

export function AiUsagePanel({ slug }: AiUsagePanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [periodKey, setPeriodKey] = useState<PeriodKey>('THIS_MONTH');
  const [reloadToken, setReloadToken] = useState(0);
  const [report, setReport] = useState<UsageReport | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);
  const [personFilter, setPersonFilter] = useState<{ userId: number; label: string } | null>(null);
  const [walletOpen, setWalletOpen] = useState<ReadonlySet<number>>(() => new Set());
  const [creditFor, setCreditFor] = useState<PersonUsageRow | null>(null);
  const notify = useNotify();
  const [usageView, setUsageView] = useUrlState(AI_USAGE_VIEW);

  const toggleWallet = (userId: number) =>
    setWalletOpen((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const period = useMemo(() => periodFor(periodKey), [periodKey]);
  const instants = useMemo(() => localDayInstants(period.from, period.to), [period]);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .aiUsage(slug, {
        from: instants.from,
        to: instants.to,
        ...(personFilter ? { userId: personFilter.userId } : {}),
      })
      .then((body) => {
        if (cancelled) return;
        setReport(body);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setReport(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, instants, personFilter, reloadToken]);

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('aiUsage.loadError', 'Could not load AI usage.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }

  const empty = report !== null && report.features.length === 0 && report.people.length === 0;

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="ai-usage-period-label">
            {t('dashboard.period.label', 'Period')}
          </InputLabel>
          <Select
            labelId="ai-usage-period-label"
            value={periodKey}
            label={t('dashboard.period.label', 'Period')}
            onChange={(event) => setPeriodKey(event.target.value as PeriodKey)}
          >
            {PERIOD_KEYS.map((key) => (
              <MenuItem key={key} value={key}>
                {t(`dashboard.period.${key}`, key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {personFilter ? (
          <Chip
            label={t('aiUsage.showing', 'Showing {{person}}', { person: personFilter.label })}
            onDelete={() => setPersonFilter(null)}
          />
        ) : null}
      </Stack>

      {report ? (
        <>
          <Typography variant="body2" color="text.secondary">
            {t('aiUsage.range', '{{from}} – {{to}}', {
              from: formatDate(report.from, locale),
              to: formatDate(report.to, locale),
            })}
          </Typography>
          <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 160px' }}>
              <KpiCard
                label={t('aiUsage.kpi.requests', 'Requests')}
                value={formatNumber(report.requests, locale)}
              />
            </Box>
            <Box sx={{ flex: '1 1 160px' }}>
              <KpiCard
                label={t('aiUsage.kpi.failed', 'Failed')}
                value={formatNumber(report.failed, locale)}
              />
            </Box>
            <Box sx={{ flex: '1 1 160px' }}>
              <KpiCard
                label={t('aiUsage.kpi.totalTokens', 'Total tokens')}
                value={formatNumber(report.totalTokens, locale)}
              />
            </Box>
          </Stack>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('common.loading', 'Loading')}
        </Typography>
      )}

      {empty ? (
        <EmptyState surface title={t('aiUsage.empty', 'No AI usage in this period.')} />
      ) : null}

      <FilterTabs
        ariaLabel={t('aiUsage.tabs.aria', 'AI usage sections')}
        active={usageView.view}
        onChange={(next) => {
          const view = oneOf(AI_USAGE_VIEWS)(next);
          if (view) setUsageView({ view });
        }}
        tabs={[
          { key: 'features', label: t('aiUsage.tabs.features', 'By feature') },
          { key: 'people', label: t('aiUsage.tabs.people', 'By person') },
        ]}
      />

      {report && !empty && usageView.view === 'features' ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={t('aiUsage.tableAria.features', 'AI usage by feature')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('aiUsage.col.feature', 'Feature')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.requests', 'Requests')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.failed', 'Failed')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.input', 'Input')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.output', 'Output')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.cacheRead', 'Cache read')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.cacheWrite', 'Cache write')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.totalTokens', 'Total tokens')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.cost', 'Cost')}</TableCell>
                <AtOurPriceHeader featureTable />
                <TableCell align="right">{t('aiUsage.col.charge', 'Est. charge')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.features.map((row) => (
                <TableRow key={row.feature} hover>
                  <TableCell>
                    {t(`aiUsage.feature.${featureCode(row.feature)}`, featureFallback(row.feature))}
                  </TableCell>
                  <TableCell align="right">
                    <Amount count={row.requests} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount count={row.failed} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount count={row.inputTokens} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount count={row.outputTokens} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount count={row.cacheReadTokens} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount count={row.cacheWriteTokens} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount count={row.totalTokens} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount micro={row.costMicro} currency={row.currency} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <FeatureAtOurPrice row={row} locale={locale} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount
                      minor={row.estimatedChargeMinor}
                      currency={row.currency}
                      locale={locale}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      {report && !empty && usageView.view === 'people' ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={t('aiUsage.tableAria.people', 'AI usage by person')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('aiUsage.col.person', 'Person')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.requests', 'Requests')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.failed', 'Failed')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.totalTokens', 'Total tokens')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.cost', 'Cost')}</TableCell>
                <AtOurPriceHeader />
                <TableCell align="right">{t('aiUsage.col.walletUsage', 'Wallet usage')}</TableCell>
                <TableCell align="right">{t('aiUsage.col.balance', 'Balance')}</TableCell>
                <TableCell>{t('aiUsage.col.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.people.map((row) => {
                const wallet = row.wallet;
                const detailId = `ai-wallet-${row.userId}`;
                const expanded = wallet !== undefined && walletOpen.has(row.userId);
                return (
                  <Fragment key={row.userId}>
                    <TableRow hover>
                      <TableCell>
                        <Typography variant="body2">{personLabel(row)}</Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {row.email}
                        </Typography>
                        {row.aiSuspendedAt ? (
                          <SuspendedChip at={row.aiSuspendedAt} locale={locale} />
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <Amount count={row.requests} locale={locale} />
                      </TableCell>
                      <TableCell align="right">
                        <Amount count={row.failed} locale={locale} />
                      </TableCell>
                      <TableCell align="right">
                        <Amount count={row.totalTokens} locale={locale} />
                      </TableCell>
                      <TableCell align="right">
                        <Amount micro={row.costMicro} currency={row.currency} locale={locale} />
                      </TableCell>
                      <TableCell align="right">
                        <AtOurPrice row={row} locale={locale} />
                      </TableCell>
                      <TableCell align="right">
                        <Amount
                          minor={row.estimatedChargeMinor}
                          currency={row.currency}
                          locale={locale}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {wallet ? (
                          <WalletAmount
                            minor={wallet.balanceMinor}
                            wallet={wallet}
                            locale={locale}
                          />
                        ) : (
                          NOT_SENT
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            onClick={() =>
                              setPersonFilter({ userId: row.userId, label: personLabel(row) })
                            }
                          >
                            {t('aiUsage.details', 'Details')}
                          </Button>
                          {wallet ? (
                            <Button
                              size="small"
                              aria-expanded={expanded}
                              aria-controls={detailId}
                              onClick={() => toggleWallet(row.userId)}
                            >
                              {t('aiUsage.wallet.toggle', 'Wallet')}
                            </Button>
                          ) : null}
                          {row.aiCreditAvailable === true ? (
                            <Button size="small" onClick={() => setCreditFor(row)}>
                              {t('aiUsage.credit.action', 'Add AI credit…')}
                            </Button>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {wallet ? (
                      <TableRow id={detailId} hidden={!expanded}>
                        <TableCell colSpan={9}>
                          {expanded ? <WalletDetail wallet={wallet} locale={locale} /> : null}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
      <AiCreditDialog
        open={creditFor !== null}
        slug={slug}
        userId={creditFor?.userId ?? 0}
        person={creditFor ? personLabel(creditFor) : ''}
        walletCurrency={creditFor?.wallet?.currency ?? undefined}
        onClose={() => setCreditFor(null)}
        onCredited={(result) => {
          setCreditFor(null);
          notify.success(
            t('aiUsage.credit.added', 'Added {{amount}}. Their AI balance is {{balance}}.', {
              amount: formatMoney(result.amountMinor, result.currency, locale),
              balance: formatMoney(result.balanceMinor, result.currency, locale),
            }),
          );
          setReloadToken((n) => n + 1);
        }}
      />
    </Stack>
  );
}
